import {
  applyAllDesignFixes,
  applyDesignFix,
  checkDesign,
  countDesignProblems,
  DESIGN_CHECK_CATEGORY_LABELS,
  type DesignCheckCategory,
  type DesignCheckOptions,
  type DesignFinding,
} from "@cva/shared";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  ShieldCheck,
  Wand2,
  X,
} from "lucide-react";
import { useDeferredValue, useMemo } from "react";
import { toast } from "sonner";
import type { EditorSearchCommand } from "../components/search-palette";
import { cn } from "../lib/utils";
import { useEnv } from "./env";
import { useEditor, useEditorApi } from "./store";

/**
 * "Design prüfen": brand, contrast, size and print findings of the open
 * document (packages/shared/src/brand/check.ts), live while editing. Fixes
 * go through `store.update`, so each is one undo step and reaches the others
 * as live ops.
 */

const CATEGORY_ORDER: DesignCheckCategory[] = [
  "contrast",
  "color",
  "font",
  "size",
  "link",
  "print",
];

function useDesignFindings(): { findings: DesignFinding[]; options: DesignCheckOptions } {
  const doc = useDeferredValue(useEditor((s) => s.doc));
  const tokens = useEditor((s) => s.brand);
  const env = useEnv();
  const options = useMemo<DesignCheckOptions>(
    () => ({
      tokens,
      assets: Object.fromEntries(
        [...env.assets.values()].map((asset) => [
          asset.id,
          { width: asset.width, height: asset.height, name: asset.name },
        ]),
      ),
    }),
    [tokens, env.assets],
  );
  const findings = useMemo(
    () => (doc.kind === "design" ? checkDesign(doc, options) : []),
    [doc, options],
  );
  return { findings, options };
}

const SEVERITY_ICON = {
  error: <AlertCircle className="size-4 shrink-0 text-red-600" />,
  warning: <AlertTriangle className="size-4 shrink-0 text-amber-500" />,
  info: <Info className="size-4 shrink-0 text-zinc-400" />,
};

/** ⌘K entry that opens the panel. */
export function designCheckSearchAction(run: () => void): EditorSearchCommand {
  return {
    id: "design-check",
    title: "Design prüfen",
    subtitle: "Marke, Kontrast, Schriftgröße und Druck",
    keywords: ["prüfen", "check", "marke", "brand", "kontrast", "barrierefrei", "barrierefreiheit"],
    icon: <ShieldCheck />,
    run,
  };
}

export function DesignCheckButton({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const store = useEditorApi();
  const readOnly = useEditor((s) => s.readOnly);
  const kind = useEditor((s) => s.doc.kind);
  const hasBrand = useEditor((s) => s.brand !== null);
  const { findings, options } = useDesignFindings();
  if (kind !== "design") return null;
  const problems = countDesignProblems(findings);
  const fixable = findings.filter((finding) => finding.fix);

  const select = (finding: DesignFinding) => {
    const state = store.getState();
    if (finding.pageIndex !== undefined && finding.pageIndex !== state.pageIndex) {
      state.goToPage(finding.pageIndex);
    }
    if (finding.elementId && state.doc.elements[finding.elementId]) {
      store.getState().select([finding.elementId]);
    }
  };
  const fixOne = (finding: DesignFinding) => {
    const fix = finding.fix;
    if (!fix) return;
    store.getState().update((draft) => {
      applyDesignFix(draft, fix, options.tokens ?? null);
    });
  };
  const fixAll = () => {
    let count = 0;
    store.getState().update((draft) => {
      count = applyAllDesignFixes(draft, options);
    });
    toast.success(count === 1 ? "1 Problem behoben" : `${count} Probleme behoben`, {
      description: "Mit ⌘Z machst du alles in einem Schritt rückgängig.",
    });
  };

  const groups = CATEGORY_ORDER.map((category) => ({
    category,
    items: findings.filter((finding) => finding.category === category),
  })).filter((group) => group.items.length > 0);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          title="Design prüfen: Marke, Kontrast und Lesbarkeit"
          aria-pressed={open}
          className={cn(
            "relative inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-100 [&_svg]:size-4",
            open && "bg-brand-50 text-brand-700 hover:bg-brand-100",
          )}
        >
          <ShieldCheck />
          <span className="hidden 2xl:inline">Prüfen</span>
          {problems > 0 ? (
            <span
              className="rounded-full bg-amber-500 px-1.5 text-[11px] leading-5 font-semibold text-white"
              data-testid="design-check-count"
            >
              {problems}
            </span>
          ) : null}
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="bottom"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          onOpenAutoFocus={(event) => event.preventDefault()}
          // Stays open while editing: the list updates live.
          onInteractOutside={(event) => event.preventDefault()}
          className="z-50 flex max-h-[calc(100vh-88px)] w-96 flex-col rounded-xl border border-zinc-200 bg-white shadow-xl outline-none"
          aria-label="Design prüfen"
        >
          <div className="flex items-center gap-2 border-b border-zinc-100 px-4 py-3">
            <ShieldCheck className="size-4 text-brand-600" />
            <h2 className="flex-1 text-sm font-semibold text-zinc-900">Design prüfen</h2>
            {!readOnly && fixable.length > 1 ? (
              <button
                type="button"
                onClick={fixAll}
                className="inline-flex items-center gap-1 rounded-md bg-brand-600 px-2 py-1 text-xs font-medium text-white hover:bg-brand-700"
              >
                <Wand2 className="size-3.5" /> Alle beheben
              </button>
            ) : null}
            <PopoverPrimitive.Close
              aria-label="Schließen"
              className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100"
            >
              <X className="size-4" />
            </PopoverPrimitive.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
            {problems === 0 ? (
              <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
                <CheckCircle2 className="size-9 text-emerald-500" />
                <p className="text-sm font-semibold text-emerald-700">
                  {hasBrand ? "Alles im Brand ✓" : "Alles gut lesbar ✓"}
                </p>
                <p className="text-xs text-zinc-500">
                  {hasBrand
                    ? "Farben, Schriften, Kontraste und Größen passen."
                    : "Kontraste und Größen passen. Mit einem Brand Kit prüfen wir auch Farben und Schriften."}
                </p>
              </div>
            ) : null}
            {groups.map((group) => (
              <section key={group.category} className="mb-2">
                <h3 className="px-2 pt-2 pb-1 text-[11px] font-semibold text-zinc-700">
                  {DESIGN_CHECK_CATEGORY_LABELS[group.category]} · {group.items.length}
                </h3>
                <ul>
                  {group.items.map((finding) => (
                    <li
                      key={finding.id}
                      className="group flex items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-zinc-50"
                    >
                      <span className="pt-0.5">{SEVERITY_ICON[finding.severity]}</span>
                      <button
                        type="button"
                        onClick={() => select(finding)}
                        disabled={!finding.elementId && finding.pageIndex === undefined}
                        className="min-w-0 flex-1 text-left text-xs leading-snug text-zinc-700 disabled:cursor-default"
                        title={finding.elementId ? "Element auswählen" : undefined}
                      >
                        {finding.message}
                        {finding.fix ? (
                          <span className="mt-0.5 block text-[11px] text-zinc-400">
                            Vorschlag: {finding.fix.label}
                          </span>
                        ) : null}
                      </button>
                      {finding.fix && !readOnly ? (
                        <button
                          type="button"
                          onClick={() => fixOne(finding)}
                          title={finding.fix.label}
                          className="shrink-0 rounded-md border border-zinc-200 px-1.5 py-0.5 text-[11px] font-medium text-zinc-700 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                        >
                          Beheben
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
          {!hasBrand ? (
            <p className="border-t border-zinc-100 px-4 py-2 text-[11px] text-zinc-500">
              Ohne Brand Kit prüfen wir nur Kontrast, Größen und Druck.
            </p>
          ) : null}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
